/**
 * Utility to get HubSpot context from iframe
 * When the app runs inside HubSpot, we can get user and portal info
 */

export function getHubSpotContext() {
  try {
    // Check if we're in an iframe
    const isInIframe = window.self !== window.top;

    if (!isInIframe) {
      console.log('Not running in iframe');
      return null;
    }

    // Try to get context from URL parameters (if passed by HubSpot extension)
    const urlParams = new URLSearchParams(window.location.search);
    const hubspotUserId = urlParams.get('userId') || urlParams.get('hubspotUserId');
    const hubspotPortalId = urlParams.get('portalId') || urlParams.get('hubspotPortalId');
    const hubspotContactId = urlParams.get('contactId') || urlParams.get('hubspotContactId');

    // Signed, short-lived handoff token — the ONLY trusted proof of identity.
    // Kept in sessionStorage (per-tab, cleared on close) so a reload within its
    // lifetime still authenticates without re-opening the card.
    const handoffToken = urlParams.get('st');
    if (handoffToken) {
      sessionStorage.setItem('hubspot_handoff_token', handoffToken);
    }

    if (hubspotUserId || hubspotPortalId || hubspotContactId || handoffToken) {
      const context = {
        userId: hubspotUserId,
        portalId: hubspotPortalId,
        contactId: hubspotContactId,
        handoffToken: handoffToken || sessionStorage.getItem('hubspot_handoff_token')
      };

      // Store in localStorage for future use
      if (hubspotUserId) localStorage.setItem('hubspot_user_id', hubspotUserId);
      if (hubspotPortalId) localStorage.setItem('hubspot_portal_id', hubspotPortalId);
      if (hubspotContactId) localStorage.setItem('hubspot_contact_id', hubspotContactId);

      return context;
    }

    // Try to get from localStorage
    const storedUserId = localStorage.getItem('hubspot_user_id');
    const storedPortalId = localStorage.getItem('hubspot_portal_id');
    const storedContactId = localStorage.getItem('hubspot_contact_id');

    const storedHandoffToken = sessionStorage.getItem('hubspot_handoff_token');

    if (storedUserId || storedPortalId || storedContactId) {
      return {
        userId: storedUserId,
        portalId: storedPortalId,
        contactId: storedContactId,
        handoffToken: storedHandoffToken
      };
    }

    // Try to get from session storage
    const sessionUserId = sessionStorage.getItem('hubspot_user_id');
    const sessionPortalId = sessionStorage.getItem('hubspot_portal_id');
    const sessionContactId = sessionStorage.getItem('hubspot_contact_id');

    if (sessionUserId || sessionPortalId || sessionContactId) {
      return {
        userId: sessionUserId,
        portalId: sessionPortalId,
        contactId: sessionContactId,
        handoffToken: storedHandoffToken
      };
    }

    return null;
  } catch (error) {
    console.error('Error getting HubSpot context:', error);
    return null;
  }
}

export function setHubSpotContext(userId, portalId) {
  try {
    if (userId) {
      localStorage.setItem('hubspot_user_id', userId);
      sessionStorage.setItem('hubspot_user_id', userId);
    }
    if (portalId) {
      localStorage.setItem('hubspot_portal_id', portalId);
      sessionStorage.setItem('hubspot_portal_id', portalId);
    }
  } catch (error) {
    console.error('Error setting HubSpot context:', error);
  }
}
